import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getFormValues, Field } from 'redux-form'

import { getFeatureFlag } from 'ggx-service/feature-flag/selectors'

import { StyledPriceBreakdown } from './order-price-breakdown.component.styles'
import { formatCurrency } from 'ggx-service/i18n/format-currency.service'
import { getLocale } from 'ggx-src/legacy/utils/locale'
import { DELIVERY_FORM_NAME } from 'ggx-src/delivery/order/order-form/order-form.component'

import { CouponSelection } from './coupon-selection.component'

const OrderPriceBreakdown = ({
  baseCharge,
  additionalCharges,
  total,
  discount,
  tip,
}) => {
  const [t] = useTranslation()
  const _addTranslationKeyToEachPriceBreakdown = priceBreakdown => {
    const mapPriceTypeAndTranslation = {
      base_charge:
        'orderDetails.title.shippingInfo.priceBreakdown.deliveryCharge',
      speed_surcharge:
        'orderDetails.shippingInfo.priceBreakdown.fourHourCharge',
      night_surcharge:
        'orderDetails.shippingInfo.priceBreakdown.nightSurcharge',
      total: 'orderDetails.shippingInfo.priceBreakdown.total',
      tip: 'common__courier_tip',
    }

    const locale = getLocale()

    return priceBreakdown.map(item => {
      // Delivery only supports Hong Kong currently
      let customSurchargeTranslation

      switch (locale) {
        case 'en-hk':
          customSurchargeTranslation = item?.extras?.en_HK
          break

        case 'zh-hk':
          customSurchargeTranslation = item?.extras?.zh_HK
          break

        default:
          break
      }

      const isCustomSurcharge = item.type.startsWith('custom_surcharge')

      return {
        amount: item.amount,
        translationKey: isCustomSurcharge
          ? customSurchargeTranslation
          : mapPriceTypeAndTranslation[item.type],
      }
    })
  }

  const priceBreakdown = [
    ...(baseCharge ? [{ type: 'base_charge', amount: baseCharge }] : []),
    ...additionalCharges,
    ...(tip ? [{ type: 'tip', amount: tip }] : []),
  ]
  const priceBreakdownWithTranslation = _addTranslationKeyToEachPriceBreakdown(
    priceBreakdown
  )

  const featureFlagEnableCouponFeature = useSelector(state =>
    getFeatureFlag({ state, featureFlag: 'enable_coupon_feature' })
  )
  const userVouchersDiscount = discount?.user_vouchers?.[0]?.discount
  const deliveryFormValues = useSelector(state =>
    getFormValues(DELIVERY_FORM_NAME)(state)
  )

  return (
    <div>
      {priceBreakdownWithTranslation.map((item, index) => (
        <StyledPriceBreakdown key={`priceBreakdown_${index}`}>
          <span>{t(item.translationKey)}</span>
          <span>{formatCurrency({ amount: item.amount })}</span>
        </StyledPriceBreakdown>
      ))}
      {featureFlagEnableCouponFeature &&
        (deliveryFormValues ? (
          <StyledPriceBreakdown>
            <span>{t('coupon')}</span>
            <span>
              <Field
                name="user_voucher_ids"
                defaultValue={null}
                component={CouponSelection}
                total={Number(total)}
                discount={
                  userVouchersDiscount && -Math.abs(userVouchersDiscount)
                }
              />
            </span>
          </StyledPriceBreakdown>
        ) : (
          userVouchersDiscount && (
            <StyledPriceBreakdown>
              <span>{t('coupon')}</span>
              <span>
                {formatCurrency({ amount: -Math.abs(userVouchersDiscount) })}
              </span>
            </StyledPriceBreakdown>
          )
        ))}
    </div>
  )
}

OrderPriceBreakdown.propTypes = {
  baseCharge: PropTypes.string,
  additionalCharges: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      amount: PropTypes.string,
    })
  ),
  total: PropTypes.string,
  discount: PropTypes.object,
  tip: PropTypes.string,
}

OrderPriceBreakdown.defaultProps = {
  additionalCharges: [],
}

export { OrderPriceBreakdown }
